import { useMutation, useQuery } from "@tanstack/react-query";
import { REFRESH_TOKEN_URL, SUPER_ADMIN_LOGIN_URL } from "./url";
import { getNavigate } from "../Navigation/Navigation";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../utils/keys";
import { strings } from "../strings/strings";

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
}

export async function fetchClient(url, options = {}, isRetry = false) {
  const { method = "GET", body, data, params, headers = {}, isFormData, ...rest } = options;
  let requestUrl = url;
  const accessToken = localStorage.getItem(ACCESS_TOKEN);

  const requestHeaders = {
    ...headers,
  };

  if (!requestHeaders["Authorization"] && accessToken && url !== REFRESH_TOKEN_URL && url !== SUPER_ADMIN_LOGIN_URL) {
    requestHeaders["Authorization"] = `Bearer ${accessToken}`;
  }

  if (!isFormData) {
    requestHeaders["Content-Type"] = "application/json";
  }

  const requestOptions = {
    method,
    headers: requestHeaders,
    ...rest,
  };

  if (isFormData) {
    requestOptions.body = data;
  } else if (body || data) {
    requestOptions.body = JSON.stringify(body || data);
  }

  if (params) {
    const queryString = new URLSearchParams(params).toString();
    requestUrl = `${url}?${queryString}`;
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${requestUrl}`, requestOptions);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));

      const error = new Error(errorBody?.message);

      error.status = response.status;
      error.code = errorBody?.code;
      error.apiMessage = errorBody?.message;
      error.response = errorBody;

      if (response.status === 401 && url !== REFRESH_TOKEN_URL && url !== SUPER_ADMIN_LOGIN_URL && !isRetry) {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const refreshed = await refreshTokenApi();
            const newAccessToken = refreshed?.results?.accessToken;
            processQueue(null, newAccessToken);
            isRefreshing = false;
            const retryOptions = {
              ...options,
              headers: {
                ...(options.headers || {}),
                Authorization: `Bearer ${newAccessToken}`,
              },
            };
            return fetchClient(url, retryOptions, true);
          } catch (refreshError) {
            processQueue(refreshError, null);
            isRefreshing = false;
            throw refreshError;
          }
        }

        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              if (token) {
                const retryOptions = {
                  ...options,
                  headers: {
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                  },
                };
                resolve(fetchClient(url, retryOptions, true));
              } else {
                reject(new Error(strings.tokenRefreshFailed));
              }
            },
            reject: (err) => reject(err),
          });
        });
      }

      if (response.status === 401 && url !== SUPER_ADMIN_LOGIN_URL) {
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        localStorage.removeItem("role");
        localStorage.removeItem(strings.adminData);
        localStorage.removeItem("otp_verified");
        getNavigate()("/login");
      }

      throw error;
    }

    return await response.json();
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error(strings.requestaborted);
    }
    throw error;
  }
}

export const useAppQuery = ({
  queryKey,
  url,
  method = "GET",
  isFormData = false,
  data = null,
  params = null,
  options = {},
  enabled = true,
}) => {
  return useQuery({
    queryKey,
    queryFn: () => fetchClient(url, { method, data, isFormData, params }),
    retry: 3,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000,
    cacheTime: 2 * 60 * 1000,
    enabled,
    ...options,
  });
};

export const useAppMutation = () => {
  return useMutation({
    mutationFn: ({ url, method = "POST", data, isFormData = false }) => fetchClient(url, { method, data, isFormData }),
    retry: false,
    onError: (error) => {
      console.error("Mutation Error:", error);
    },
  });
};

export async function refreshTokenApi() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);

  try {
    if (!refreshToken) throw new Error(strings.missingrefreshtoken);

    const data = { refreshToken: refreshToken };

    const res = await fetchClient(REFRESH_TOKEN_URL, {
      method: "POST",
      data,
    });

    const newAccessToken = res?.results?.accessToken;
    const newRefreshToken = res?.results?.refreshToken;

    if (!newAccessToken) throw new Error(strings.invalidrefreshresponse);

    localStorage.setItem(ACCESS_TOKEN, newAccessToken);
    localStorage.setItem(REFRESH_TOKEN, newRefreshToken);

    return res;
  } catch (err) {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem("role");
    localStorage.removeItem(strings.adminData);
    localStorage.removeItem("otp_verified");
    getNavigate()("/login");
    throw err;
  }
}
